// Sailboats, motorboats, the cruise ship, gulls and sea animation.
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { PI } from '../lib/math.js';
import { pick, rnd, rr } from '../lib/random.js';
import { scene } from '../core/scene.js';
import { M, P, Ph, add, canvasTex } from '../lib/factory.js';
import { gloriesMats } from '../materials/surfaces.js';
import { seaNormal, waveTime } from './sea.js';
import { V } from './sagrada.js';
import { standSpots, sups } from '../people/crowd.js';
import { animateBirds } from '../fauna/birds.js';
import { profile } from '../vehicles/scooter.js';
import { state } from '../core/state.js';

export let boats, gulls, animateSea;

export function buildSeaLife() {
  animateSea = function animateSea(time, dt) {
    for (const b of boats) {
      b.a += b.v * dt;
      const x = Math.cos(b.a) * b.r,
        z = b.cz + Math.sin(b.a) * b.r * 0.35 + 60;
      b.g.position.set(x, -0.35 + Math.sin(time * 1.3 + b.r) * 0.06, z);
      b.g.rotation.y = Math.atan2(
        -Math.sin(b.a) * b.r * Math.sign(b.v),
        Math.cos(b.a) * b.r * 0.35 * Math.sign(b.v),
      );
      b.g.rotation.z = Math.sin(time * 0.9 + b.r) * (b.sail ? 0.06 : 0.03);
      if (b.jet) {
        b.g.position.y = -0.3 + Math.abs(Math.sin(time * 3.1)) * 0.07;
        b.g.rotation.order = 'YXZ';
        b.g.rotation.x = -0.07 + Math.sin(time * 3.1) * 0.03;
        b.g.rotation.z = Math.sign(b.v) * 0.2;
      }
    }
    for (const gl of gulls) {
      gl.a += gl.v * dt;
      const x = gl.cx + Math.cos(gl.a) * gl.r,
        z = gl.cz + Math.sin(gl.a) * gl.r;
      gl.g.position.set(x, gl.y + Math.sin(time * 0.7 + gl.ph) * 0.8, z);
      gl.g.rotation.y = Math.atan2(
        -Math.sin(gl.a) * Math.sign(gl.v),
        Math.cos(gl.a) * Math.sign(gl.v),
      );
      // flap: the inner wing leads, the outer half follows a beat later; mostly gliding with bursts of flapping
      const pw = 0.4 + 0.6 * Math.max(0, Math.sin(time * 0.35 + gl.ph)),
        f = Math.sin(time * 6 + gl.ph) * 0.45 * pw,
        fo = Math.sin(time * 6 + gl.ph - 0.9) * 0.5 * pw;
      // the mirrored wing needs the opposite angle at the shoulder; the outer half inherits the mirror
      for (const w of gl.wings) {
        w.rotation.z = (f + 0.08) * w.userData.sx;
        w.userData.outer.rotation.z = fo * 0.8 - 0.05;
      }
    }
    seaNormal.offset.set(time * 0.004, time * 0.006);
    waveTime.value = time;
    animateBirds(time);
    if (window.rimFoam) window.rimFoam.material.opacity = 0.45 + Math.sin(time * 1.1) * 0.25;
    if (state.isNight) gloriesMats.forEach((m) => m.emissive.setHSL((time * 0.04) % 1, 0.7, 0.6));
    if (window.foam) {
      window.foam.material.opacity = 0.55 + Math.sin(time * 0.8) * 0.3;
      window.foam.position.z = 63.3 + Math.sin(time * 0.8) * 0.35;
    }
  };
  // ---------- the sea: sailboats, motorboats, a cruise ship, and gulls ----------
  boats = [];
  gulls = [];
  {
    const white = P(0xf4f4f0, { roughness: 0.35, metalness: 0.1 }),
      sailMat = M(0xfafaf5, { flatShading: false, side: THREE.DoubleSide, roughness: 0.8 }),
      wood = M(0x8a6a4a, { flatShading: false });
    const hullG = profile(
      [
        [-1.6, 0.2],
        [1.4, 0.2],
        [2, 0.55],
        [-1.7, 0.6],
      ],
      0.9,
      0.05,
      0.15,
    );
    for (let k = 0; k < 7; k++) {
      const g = new THREE.Group(),
        sail = k < 5;
      add(hullG, white, 0, 0, 0, g);
      add(new THREE.BoxGeometry(0.8, 0.05, 2.6), wood, 0, 0.62, 0, g);
      if (sail) {
        add(new THREE.CylinderGeometry(0.04, 0.05, 5, 8), white, 0, 3, 0.3, g);
        const sh = new THREE.Shape();
        sh.moveTo(0, 0);
        sh.lineTo(0, 4.4);
        sh.lineTo(-2.2, 0);
        sh.closePath();
        add(new THREE.ShapeGeometry(sh).rotateY(PI / 2), sailMat, 0, 0.8, 0.25, g);
        const jib = new THREE.Shape();
        jib.moveTo(0, 0);
        jib.lineTo(0, 3.6);
        jib.lineTo(1.5, 0);
        jib.closePath();
        add(new THREE.ShapeGeometry(jib).rotateY(PI / 2), sailMat, 0, 0.75, 0.55, g);
      } else add(new RoundedBoxGeometry(0.8, 0.5, 1, 2, 0.1), white, 0, 0.9, -0.3, g);
      g.scale.setScalar(sail ? 1.1 : 0.8);
      scene.add(g);
      boats.push({
        g,
        a: rr(0, PI * 2),
        r: rr(90, 210),
        cz: rr(80, 140),
        v: (sail ? rr(0.015, 0.03) : rr(0.05, 0.08)) * (rnd() < 0.5 ? 1 : -1),
        sail,
      });
    }
    // jet ski: a pointed hull with a dark spray rail, a coloured hood and side stripe, a long black saddle, the steering
    // column with handlebar, grips and mirrors; a wake on the water and a rooster tail of spray behind it
    {
      const g = new THREE.Group(),
        body = Ph(0xf2f0ea, { roughness: 0.3 }),
        accent = Ph(0xd9412c, { roughness: 0.3 }),
        dark = M(0x2a2c30, { flatShading: false }),
        black = M(0x151618, { flatShading: false, roughness: 0.6 });
      add(
        profile(
          [
            [-0.95, 0.02],
            [0.7, 0.02],
            [1.12, 0.2],
            [0.98, 0.36],
            [0.3, 0.4],
            [-0.95, 0.38],
          ],
          0.74,
          0.03,
          0.06,
        ),
        body,
        0,
        0,
        0,
        g,
      );
      add(
        profile(
          [
            [-0.97, -0.04],
            [0.75, -0.04],
            [1, 0.1],
            [-0.97, 0.1],
          ],
          0.8,
          0.02,
          0.03,
        ),
        dark,
        0,
        0,
        0,
        g,
      );
      add(
        new RoundedBoxGeometry(0.6, 0.16, 0.62, 3, 0.07).rotateX(-0.25),
        accent,
        0,
        0.46,
        0.55,
        g,
      );
      for (const sx of [-1, 1])
        add(new THREE.BoxGeometry(0.01, 0.05, 1.4), accent, sx * 0.395, 0.24, -0.05, g);
      add(new RoundedBoxGeometry(0.34, 0.16, 1.05, 3, 0.07), black, 0, 0.5, -0.38, g);
      add(new THREE.CylinderGeometry(0.035, 0.045, 0.34, 8).rotateX(-0.45), dark, 0, 0.62, 0.3, g);
      add(
        new THREE.CylinderGeometry(0.014, 0.014, 0.62, 8).rotateZ(PI / 2),
        dark,
        0,
        0.78,
        0.23,
        g,
      );
      for (const sx of [-1, 1])
        add(
          new THREE.CylinderGeometry(0.024, 0.024, 0.12, 8).rotateZ(PI / 2),
          black,
          sx * 0.32,
          0.78,
          0.23,
          g,
        );
      state.jetSki = g;
      const wakeTex = canvasTex(32, 128, (c) => {
        const gr = c.createLinearGradient(0, 0, 0, 128);
        gr.addColorStop(0, 'rgba(255,255,255,.9)');
        gr.addColorStop(1, 'rgba(255,255,255,0)');
        c.fillStyle = gr;
        c.fillRect(0, 0, 32, 128);
      });
      add(
        new THREE.PlaneGeometry(1.2, 9).rotateX(-PI / 2).translate(0, 0.02, -5.2),
        new THREE.MeshBasicMaterial({ map: wakeTex, transparent: true, depthWrite: false }),
        0,
        0,
        0,
        g,
      ).castShadow = false;
      // rooster tail: two crossed soft planes fanning up from the stern
      const sprayTex = canvasTex(64, 64, (c) => {
        for (let k = 0; k < 40; k++) {
          const x = 32 + rr(-20, 20) * (k / 40 + 0.3),
            y = 64 - k * 1.4 - rr(0, 6),
            r = rr(4, 9),
            gr = c.createRadialGradient(x, y, 0, x, y, r);
          gr.addColorStop(0, 'rgba(255,255,255,.75)');
          gr.addColorStop(1, 'rgba(255,255,255,0)');
          c.fillStyle = gr;
          c.fillRect(x - r, y - r, 2 * r, 2 * r);
        }
      });
      const sprayMat = new THREE.MeshBasicMaterial({
        map: sprayTex,
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      for (const ry of [0, PI / 2]) {
        const m = add(
          new THREE.PlaneGeometry(1.1, 1.3).translate(0, 0.55, 0).rotateX(-0.6).rotateY(ry),
          sprayMat,
          0,
          0,
          -1.05,
          g,
        );
        m.castShadow = false;
      }
      scene.add(g);
      boats.push({ g, a: 0, r: 36, cz: 18, v: 0.09, sail: false, jet: true });
    }
    // a stand-up paddle board: rounded nose, wide middle, squarer tail, thin with soft rails; a grooved deck pad and a fin
    const supShape = (L, W, inset = 0) => {
      const sh = new THREE.Shape(),
        h = L / 2 - inset,
        w = W / 2 - inset,
        t = w * 0.72;
      sh.moveTo(-t, -h);
      sh.lineTo(t, -h);
      sh.quadraticCurveTo(w, -h * 0.4, w, 0);
      sh.quadraticCurveTo(w, h * 0.55, 0, h);
      sh.quadraticCurveTo(-w, h * 0.55, -w, 0);
      sh.quadraticCurveTo(-w, -h * 0.4, -t, -h);
      return sh;
    };
    const supGeo = new THREE.ExtrudeGeometry(supShape(2.7, 0.72), {
      depth: 0.06,
      bevelEnabled: true,
      bevelSize: 0.03,
      bevelThickness: 0.03,
      bevelSegments: 3,
      curveSegments: 16,
    })
      .rotateX(PI / 2)
      .translate(0, 0.06, 0);
    const supPadGeo = new THREE.ShapeGeometry(supShape(1.1, 0.6, 0.03), 8)
      .rotateX(-PI / 2)
      .translate(0, 0.095, -0.25);
    const supPadTex = canvasTex(32, 64, (c) => {
      c.fillStyle = '#fff';
      c.fillRect(0, 0, 32, 64);
      c.fillStyle = 'rgba(0,0,0,.35)';
      for (let y = 2; y < 64; y += 5) c.fillRect(0, y, 32, 1.5);
    });
    const supFinGeo = new THREE.BufferGeometry().setFromPoints([
      V(0, 0, -1.25),
      V(0, 0, -0.95),
      V(0, -0.22, -1.22),
    ]);
    supFinGeo.computeVertexNormals();
    // stand-up paddle boards gliding slowly in loops; the paddle hangs from a pivot at hand height and is swept
    // stroke by stroke, switching sides, in time with the rider's paddling clip
    for (const [x, z, r] of [
      [-6, 69, 0.4],
      [19, 71, -0.8],
      [-24, 70, 2],
    ]) {
      const g = new THREE.Group();
      g.position.set(x, -0.3, z);
      g.rotation.y = r;
      scene.add(g);
      add(
        supGeo,
        Ph(pick([0xf2c014, 0x2f6fd0, 0xf2f0ea, 0x3fb7a6, 0xe86aa6]), { roughness: 0.3 }),
        0,
        0,
        0,
        g,
      );
      add(supPadGeo, M(0x3a3d42, { map: supPadTex, flatShading: false }), 0, 0, 0, g);
      add(supFinGeo, M(0x222222), 0, 0, 0, g);
      const sp = { x: 0, y: 0.05, z: 0, ry: 0, parent: g, sup: true };
      standSpots.push(sp);
      const pivot = new THREE.Group();
      pivot.position.set(0.12, 0.72, 0.3);
      g.add(pivot);
      add(
        new THREE.CylinderGeometry(0.015, 0.015, 1.7, 6).translate(0, -0.55, 0),
        M(0x222222),
        0,
        0,
        0,
        pivot,
      );
      add(
        new THREE.BoxGeometry(0.16, 0.4, 0.02).translate(0, -1.45, 0),
        M(0x222222),
        0,
        0,
        0,
        pivot,
      );
      const rad = rr(5, 8),
        dir = rnd() < 0.5 ? 1 : -1;
      // the whole loop stays in open water: the shoreline is at z ≈ 63, so its nearest point is kept beyond z = 67
      sups.push({
        g,
        sp,
        pivot,
        cx: x - Math.cos(r) * rad,
        cz: Math.max(z - Math.sin(r) * rad, 67 + rad),
        rad,
        ang: r,
        w: (dir * 0.4) / rad,
        ph: rr(0, 6),
      });
    }
    // cruise ship far out on the horizon
    const ship = new THREE.Group();
    ship.position.set(-230, -0.35, 330);
    ship.rotation.y = 0.3;
    scene.add(ship);
    add(
      profile(
        [
          [-22, 0],
          [20, 0],
          [26, 5],
          [-23, 5.5],
        ],
        7,
        0.2,
        1.2,
      ),
      P(0xf2f2f0, { roughness: 0.4 }),
      0,
      0,
      0,
      ship,
    );
    add(new THREE.BoxGeometry(7.2, 1, 44), P(0x1d2a44, { roughness: 0.4 }), 0, 1.2, 0, ship);
    for (let d = 0; d < 4; d++)
      add(
        new RoundedBoxGeometry(6.2 - d * 0.6, 1.6, 34 - d * 5, 2, 0.3),
        P(0xfafaf8, { roughness: 0.4 }),
        0,
        6.3 + d * 1.6,
        -2 + d,
        ship,
      );
    add(
      new THREE.CylinderGeometry(1.4, 1.8, 4, 20),
      P(0xc8392e, { roughness: 0.4 }),
      0,
      13.5,
      -8,
      ship,
    );
    // gulls: white body with a grey mantle, yellow bill, and two-part wings (grey inner, black-tipped outer) that
    // flap with the outer half lagging behind, like the real thing
    const gullMat = M(0xf5f5f2, { flatShading: false, side: THREE.DoubleSide }),
      mantle = M(0xaab2bc, { flatShading: false, side: THREE.DoubleSide }),
      tipMat = M(0x2a2a2a, { side: THREE.DoubleSide, flatShading: false }),
      billMat = M(0xf2c230, { flatShading: false });
    const wingShape = (a, b, w0, w1) => {
      const sh = new THREE.Shape();
      sh.moveTo(a, w0 * 0.5);
      sh.lineTo(b, w1 * 0.5);
      sh.lineTo(b, -w1 * 0.5);
      sh.lineTo(a, -w0 * 0.5);
      sh.closePath();
      return new THREE.ShapeGeometry(sh).rotateX(-PI / 2);
    };
    const innerW = wingShape(0, 0.42, 0.22, 0.18),
      outerW = wingShape(0, 0.38, 0.18, 0.05),
      tipW = wingShape(0.24, 0.38, 0.1, 0.05);
    for (let k = 0; k < 14; k++) {
      const g = new THREE.Group(),
        wings = [];
      add(new THREE.CapsuleGeometry(0.06, 0.26, 4, 10).rotateX(PI / 2), gullMat, 0, 0, 0, g);
      add(new THREE.SphereGeometry(0.055, 12, 8), gullMat, 0, 0.03, 0.2, g);
      add(new THREE.ConeGeometry(0.014, 0.07, 6).rotateX(PI / 2), billMat, 0, 0.025, 0.27, g);
      add(new THREE.BoxGeometry(0.1, 0.015, 0.12).translate(0, 0, -0.2), gullMat, 0, 0, 0, g);
      for (const sx of [-1, 1]) {
        const w = new THREE.Group();
        w.position.x = sx * 0.05;
        w.scale.x = sx;
        g.add(w);
        add(innerW, mantle, 0, 0, 0, w);
        const o = new THREE.Group();
        o.position.x = 0.42;
        w.add(o);
        add(outerW, mantle, 0, 0.001, 0, o);
        add(tipW, tipMat, 0, 0.002, 0, o);
        w.userData.outer = o;
        w.userData.sx = sx;
        wings.push(w);
      }
      g.scale.setScalar(0.6); // a real gull spans ~1.4 m: about 0.75 units at this scale
      scene.add(g);
      gulls.push({
        g,
        wings,
        cx: rr(-40, 40),
        cz: rr(50, 85),
        r: rr(6, 16),
        y: rr(6, 16),
        a: rr(0, PI * 2),
        v: rr(0.25, 0.45) * (rnd() < 0.5 ? 1 : -1),
        ph: rr(0, 6),
      });
    }
  }
}
